/**
 * Normalizes a form field value for semantic comparison:
 *   - Numbers pass through as-is
 *   - Strings are trimmed so leading/trailing whitespace is ignored
 *   - null / undefined / missing keys normalize to '' so comparisons are safe
 *     even when one side is an uninitialized optional field
 */
function normalizeFieldValue(val: unknown): number | string {
  if (typeof val === 'number') return val;
  if (val == null) return '';
  return String(val).trim();
}

/**
 * Returns true when the current form values contain at least one meaningful
 * change relative to the defaults, skipping fields in excludedKeys.
 *
 * Semantic rules applied before comparison:
 *   - Leading/trailing whitespace is ignored  ("John   " === "John")
 *   - null / undefined / missing values are treated as empty string
 */
export function hasSemanticFormChanges(
  currentValues: Record<string, unknown>,
  defaultValues: Record<string, unknown>,
  excludedKeys: Set<string>,
): boolean {
  return Object.keys(currentValues).some(
    (key) =>
      !excludedKeys.has(key) &&
      normalizeFieldValue(currentValues[key]) !==
        normalizeFieldValue(defaultValues[key]),
  );
}

/** Minimal patient name shape shared by list and detail order views. */
export type OrderWithPatientName = {
  patient?: {
    firstNamePhi?: string | null;
    lastNamePhi?: string | null;
    middleInitialPhi?: string | null;
  } | null;
};

/**
 * Returns the order patient's full name (first + middle initial + last), or null if no patient.
 */
export function getOrderPatientFullName(
  order: OrderWithPatientName,
): string | null {
  const patient = order.patient;
  if (!patient) return null;
  return (
    [patient.firstNamePhi, patient.middleInitialPhi, patient.lastNamePhi]
      .filter(Boolean)
      .join(' ')
      .trim() || null
  );
}
